import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaEnvelope, FaPhone, FaWhatsapp, FaClock,
  FaCheckCircle, FaArrowRight, FaInstagram,
  FaLinkedin, FaYoutube, FaTwitter, FaCommentDots
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const SUPPORT_EMAIL = 'support@fitnession.fit';
const PHONE = '+91 9579520586';
const WA_LINK = 'https://wa.me/919579520586';

const contactTopics = [
  { label: 'Program enquiry', value: 'Program enquiry' },
  { label: 'Subscription / billing', value: 'Subscription / billing' },
  { label: 'Technical issue', value: 'Technical issue' },
  { label: 'Coach / doctor assignment', value: 'Coach / doctor assignment' },
  { label: 'Refund request', value: 'Refund request' },
  { label: 'Partnership / B2B', value: 'Partnership / B2B' },
  { label: 'Other', value: 'Other' },
];

const quickLinks = [
  { label: 'View our programs', to: '/our-program' },
  { label: 'Pricing & plans', to: '/pricing' },
  { label: 'FAQs', to: '/faq' },
  // { label: 'GLP-1 Program', href: 'https://glp1.fitnession.fit' },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.subject) e.subject = 'Please select a topic';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.trim().length < 20) e.message = 'Please write at least 20 characters';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);

    // Open mailto as fallback — replace with Supabase send-email function when ready
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || 'Not provided'}\n\n${form.message}`
    );
    const mailtoUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`[${form.subject}] from ${form.name}`)}&body=${body}`;
    window.location.href = mailtoUrl;

    // Show success after brief delay
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      toast.success('Message prepared! Your email client should open shortly.');
    }, 800);
  };

  const handleReset = () => {
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setErrors({});
    setSubmitted(false);
  };

  return (
    <div className="bg-white min-h-screen">

      {/* Hero */}
      <div className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <FaCommentDots className="text-2xl text-primary-300" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">We're Here to Help</h1>
          <p className="text-primary-200 text-lg max-w-xl mx-auto">
            Questions about our programs, billing, or coaching? Reach out — our team typically responds within 2 hours.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-12">

          {/* ── Left: Contact Info ───────────────────────────────────── */}
          <div className="space-y-6">

            {/* Contact cards */}
            {[
              {
                icon: FaWhatsapp,
                label: 'WhatsApp',
                value: PHONE,
                sub: 'Fastest response · usually within 30 min',
                href: WA_LINK,
                color: 'bg-green-50 text-green-600',
                border: 'border-green-100',
              },
              {
                icon: FaPhone,
                label: 'Call Us',
                value: PHONE,
                sub: 'Mon – Sat · 9 am – 7 pm IST',
                href: `tel:+919579520586`,
                color: 'bg-blue-50 text-blue-600',
                border: 'border-blue-100',
              },
              {
                icon: FaEnvelope,
                label: 'Email',
                value: SUPPORT_EMAIL,
                sub: 'Reply within 4 business hours',
                href: `mailto:${SUPPORT_EMAIL}`,
                color: 'bg-purple-50 text-purple-600',
                border: 'border-purple-100',
              },
            ].map((c, i) => (
              <a
                key={i}
                href={c.href}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className={`flex items-start gap-4 p-4 bg-white border ${c.border} rounded-2xl hover:shadow-md transition-shadow group`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${c.color} group-hover:scale-110 transition-transform`}>
                  <c.icon className="text-xl" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{c.label}</p>
                  <p className="font-semibold text-gray-900">{c.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{c.sub}</p>
                </div>
              </a>
            ))}

            {/* Business hours */}
            <div className="bg-gray-50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaClock className="text-gray-400" />
                <p className="font-semibold text-gray-700 text-sm">Support Hours</p>
              </div>
              <div className="space-y-1.5 text-sm">
                {[
                  ['Mon – Fri', '9:00 am – 7:00 pm IST'],
                  ['Saturday', '10:00 am – 5:00 pm IST'],
                  ['Sunday', 'WhatsApp only'],
                ].map(([day, hours], i) => (
                  <div key={i} className="flex justify-between text-gray-600">
                    <span>{day}</span>
                    <span className="font-medium text-gray-800">{hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Quick Links</p>
              <div className="space-y-2">
                {quickLinks.map((l, i) => (
                  l.to ? (
                    <Link key={i} to={l.to} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 text-sm text-gray-700 hover:text-primary-700 group transition-colors">
                      <span>{l.label}</span>
                      <FaArrowRight className="text-xs text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ) : (
                    <a key={i} href={l.href} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 text-sm text-gray-700 hover:text-primary-700 group transition-colors">
                      <span>{l.label}</span>
                      <FaArrowRight className="text-xs text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-transform" />
                    </a>
                  )
                ))}
              </div>
            </div>

            {/* Social */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Follow Us</p>
              <div className="flex gap-3">
                {[
                  { icon: FaInstagram, href: '#', label: 'Instagram' },
                  { icon: FaLinkedin, href: '#', label: 'LinkedIn' },
                  { icon: FaYoutube, href: '#', label: 'YouTube' },
                  { icon: FaTwitter, href: '#', label: 'Twitter' },
                ].map((s, i) => (
                  <a key={i} href={s.href} aria-label={s.label} className="w-10 h-10 bg-gray-100 hover:bg-primary-600 hover:text-white rounded-xl flex items-center justify-center text-gray-500 transition-colors">
                    <s.icon />
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* ── Right: Contact Form ──────────────────────────────────── */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-100 rounded-3xl shadow-lg p-8">

              {submitted ? (
                /* Success state */
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <FaCheckCircle className="text-green-500 text-4xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h2>
                  <p className="text-gray-500 mb-2">
                    Your email client should have opened with your message pre-filled.
                  </p>
                  <p className="text-gray-500 text-sm mb-8">
                    We'll reply to <span className="font-semibold text-gray-700">{form.email}</span> within 4 business hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button onClick={handleReset} className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors">
                      Send Another Message
                    </button>
                    <Link to="/" className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium text-sm transition-colors text-center">
                      Back to Home
                    </Link>
                  </div>
                </div>
              ) : (
                /* Form */
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Send Us a Message</h2>
                    <p className="text-gray-500 text-sm">Fill in the form below and we'll get back to you shortly.</p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="space-y-5">

                    {/* Name + Email */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Priya Sharma"
                          className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary-400'}`}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="priya@example.com"
                          className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary-400'}`}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Phone + Topic */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</span>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="98765 43210"
                            className="flex-1 px-4 py-3 rounded-r-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400 transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Topic <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors bg-white ${errors.subject ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary-400'}`}
                        >
                          <option value="">Select a topic...</option>
                          {contactTopics.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Describe your query in detail..."
                        className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors resize-none ${errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary-400'}`}
                      />
                      <div className="flex justify-between mt-1">
                        {errors.message
                          ? <p className="text-red-500 text-xs">{errors.message}</p>
                          : <span />
                        }
                        <p className={`text-xs ${form.message.length < 20 ? 'text-gray-400' : 'text-green-500'}`}>
                          {form.message.length} / 20 min chars
                        </p>
                      </div>
                    </div>

                    {/* Submit */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-8 py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:translate-y-0"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Sending…
                          </>
                        ) : (
                          <>Send Message <FaArrowRight className="ml-2" /></>
                        )}
                      </button>
                      <p className="text-xs text-gray-400">
                        We respond within 4 business hours · Your data is protected per our{' '}
                        <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>
                      </p>
                    </div>

                  </form>
                </>
              )}
            </div>

            {/* GLP-1 note — hidden for now */}
            {/* <div className="mt-6 bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-6 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-bold mb-1">Looking for GLP-1 Medical Support?</p>
                <p className="text-gray-300 text-sm">Our medical team handles GLP-1 programme queries separately.</p>
              </div>
              <a
                href="https://glp1.fitnession.fit"
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0 inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors"
              >
                Visit GLP-1 Site <FaArrowRight className="ml-2" />
              </a>
            </div> */}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
